export const ORDER = [
  "is-my-marketing-agency-overcharging",
  "how-to-tell-if-law-firm-marketing-is-working",
  "marketing-attribution-for-law-firms",
];

export const ARTICLES = {
  "is-my-marketing-agency-overcharging": {
    title: "Is Your Law Firm's Marketing Agency Overcharging You? How to Tell.",
    description:
      "Worried your law firm's marketing agency isn't delivering? Here's how to check — the questions to ask, the red flags to look for, and how to verify spend against signed cases.",
    read: "6 min read",
    excerpt:
      "If your reports are full of clicks and calls but you can't say what a signed case costs, the performance may simply be difficult to verify. Here's how to check.",
    blocks: [
      {
        p: [
          "Most firms don't suspect their agency because the work looks bad — they suspect it because they can't tell whether it's working. The reports are full of activity, the invoices arrive on time, and yet no one can answer a simple question: what did our last ten signed cases actually cost? That's not proof of wrongdoing. It's a reporting gap — and it's worth closing.",
        ],
      },
      {
        h: "Start with one question",
        p: [
          "Ask your agency: \"Can you tell me our cost per signed case, by channel, for last month?\" A partner focused on outcomes should be able to answer with real numbers. If the answer is impressions, rankings or \"brand awareness,\" you're being shown activity metrics, not results — and the true performance is difficult to verify from the reports alone.",
        ],
      },
      {
        h: "Read your last three reports for three phrases",
        p: [
          "Open your last three monthly reports and search for \"signed case,\" \"retained client,\" or \"cost per case.\" If those phrases never appear, your reporting is measuring the top of the funnel and stopping there — long before the only outcome that pays your firm's bills.",
        ],
      },
      {
        h: "Look for these unexplained gaps",
        list: [
          "Lead reports that don't reconcile with your intake records, with no explanation for the difference.",
          "Charges for deliverables you can't confirm were produced on the agreed schedule or scope.",
          "\"Qualified\" leads billed as qualified that were hang-ups, wrong numbers or spam.",
          "Growing spend with flat signed-case volume — and no one able to explain the gap.",
          "A vendor that won't share account access or the path from lead to signed case.",
        ],
      },
      {
        h: "How to verify it independently",
        p: [
          "You don't have to fire anyone to get answers. An independent review reconciles your own spend, invoices, lead data and retained matters — from a neutral vantage point — to calculate cost per signed case and surface anything that can't be explained. It doesn't replace your agency; it gives leadership a second, unbiased read on the work.",
        ],
      },
    ],
    note: null,
    related: ["/law-firm-marketing-audit", "/cost-per-signed-case"],
  },

  "how-to-tell-if-law-firm-marketing-is-working": {
    title: "How to Tell If Your Law Firm's Marketing Is Actually Working",
    description:
      "Stop guessing. Learn the metrics that prove your law firm's marketing is producing signed cases — and the vanity numbers that hide the truth.",
    read: "5 min read",
    excerpt:
      "Traffic and leads can climb while signed cases stay flat. Here are the metrics that actually prove your marketing is working.",
    blocks: [
      {
        p: [
          "Marketing that \"feels busy\" is not the same as marketing that works. Clicks, impressions and even lead volume can all rise while the number that matters — signed cases — stays flat. To know whether your spend is producing clients, you have to follow the money all the way to a signed retainer.",
        ],
      },
      {
        h: "Measure to the signed case, not the click",
        p: [
          "The single most important metric is cost per signed case: total marketing cost divided by the cases that produced a signed retainer, measured across your real sales cycle. Everything upstream — cost per click, cost per lead — only matters if it produces retained clients at a cost your firm can sustain.",
        ],
      },
      {
        h: "Watch the whole funnel, not just the top",
        list: [
          "Spend by channel and practice area",
          "Leads and qualified leads (real conversations, not hang-ups)",
          "Consultations booked and consult-to-retained rate",
          "Signed cases attributed back to source, campaign and keyword",
          "Cost per signed case by channel",
        ],
      },
      {
        h: "Respond fast — the data says most firms don't",
        p: [
          "Speed to a new lead is one of the biggest hidden leaks. According to Clio's 2024 Legal Trends Report, 67% of law firms fail to respond to a prospective client's email and 48% are effectively unreachable by phone. If your marketing is generating leads that intake never reaches, the problem may not be the marketing at all — and only reconciling marketing against intake will tell you which it is.",
        ],
      },
      {
        h: "Review weekly, decide with evidence",
        p: [
          "Firms that reallocate budget weekly — based on search terms, negative keywords and funnel metrics — compound results over time. If you can't answer \"which keywords produced signed cases?\", your measurement is incomplete, and any vendor comparison is guesswork.",
        ],
      },
    ],
    note: "Statistics cited from Clio's 2024 Legal Trends Report (clio.com). Figures reflect the broader legal market, not RetainProof clients.",
    related: ["/cost-per-signed-case", "/law-firm-marketing-audit"],
  },

  "marketing-attribution-for-law-firms": {
    title: "Marketing Attribution for Law Firms: Connecting Spend to Signed Cases",
    description:
      "A practical guide to closed-loop marketing attribution for law firms — the tracking stack, the sales-cycle windows, and why attribution is the key to verifying vendor performance.",
    read: "6 min read",
    excerpt:
      "Attribution is the mechanism that connects an ad click to a signed retainer. Without it, every vendor comparison is a guess.",
    blocks: [
      {
        p: [
          "Attribution is the discipline of tracing each signed case back to the ad, campaign or vendor that produced it. It's the difference between \"we spent $45,000 and signed some cases\" and \"this channel produced signed cases at $2,900 each while that one produced none.\" Without attribution, you can't hold any vendor accountable — because you can't prove what any of them delivered.",
        ],
      },
      {
        h: "The minimum tracking stack",
        list: [
          "UTM parameters and click IDs on every campaign and link",
          "Call tracking with dynamic number insertion (e.g., CallRail) per channel",
          "GA4 conversion events for post-click behavior",
          "A CRM (e.g., Clio or Lawmatics) with a lead-source field that updates to \"retained\" when a case signs",
        ],
      },
      {
        h: "Mind the attribution window",
        p: [
          "Personal-injury and plaintiff matters have long consideration cycles. A lead that clicks today may sign in 90–180 days — well past Google's default 30-day window. If your reporting closes the books at 30 days, you'll systematically under-credit the channels that produce your best cases.",
        ],
      },
      {
        h: "Where attribution breaks",
        p: [
          "Attribution fails quietly: a vendor reports leads it can't tie to a source, calls get attributed to \"direct\" or \"organic,\" and intake records never reconcile with billed lead activity. Each break is a place where spend becomes difficult to verify — and where an independent reconciliation earns its keep.",
        ],
      },
      {
        h: "Turning attribution into accountability",
        p: [
          "Good attribution isn't just a marketing tool — it's an accountability tool. Once each signed case is traced to a source, you can calculate cost per signed case by vendor, compare vendors on the same neutral basis, and challenge charges that don't hold up. That reconciliation, done independently of the vendors being paid, is the core of a marketing audit.",
        ],
      },
    ],
    note: null,
    related: ["/law-firm-marketing-audit", "/cost-per-signed-case"],
  },
};
